/**
 * Chat Reducer
 * Manages chat sessions, messages, and related state
 */

import {
	CHAT_CREATE_SESSION_REQUEST,
	CHAT_CREATE_SESSION_SUCCESS,
	CHAT_CREATE_SESSION_FAILURE,
	CHAT_SEND_MESSAGE_REQUEST,
	CHAT_SEND_MESSAGE_SUCCESS,
	CHAT_SEND_MESSAGE_FAILURE,
	CHAT_LOAD_SESSION_REQUEST,
	CHAT_LOAD_SESSION_SUCCESS,
	CHAT_LOAD_SESSION_FAILURE,
	CHAT_LOAD_USER_SESSIONS_REQUEST,
	CHAT_LOAD_USER_SESSIONS_SUCCESS,
	CHAT_LOAD_USER_SESSIONS_FAILURE,
	CHAT_SET_CURRENT_SESSION,
	CHAT_CLEAR_MESSAGES,
	CHAT_SET_AI_TYPING,
	CHAT_RESET_STATE,
	CHAT_ADD_OPTIMISTIC_MESSAGE,
	CHAT_UPDATE_MESSAGE_STATUS,
	CHAT_REMOVE_OPTIMISTIC_MESSAGE,
	CHAT_DELETE_SESSION_REQUEST,
	CHAT_DELETE_SESSION_SUCCESS,
	CHAT_DELETE_SESSION_FAILURE,
	CHAT_SET_SELECTED_MODEL
} from "../actions/actionContants";
import { ChatMessage, UserSession } from "../../services/chatService";

export interface ChatState {
	// Current session
	currentSessionId: string | null;
	currentSessionTitle: string | null;

	// Messages for current session
	messages: ChatMessage[];
	totalMessages: number;
	hasMoreMessages: boolean;

	// User sessions list
	userSessions: UserSession[];
	totalSessions: number;
	hasMoreSessions: boolean;

	// Loading states
	isCreatingSession: boolean;
	isSendingMessage: boolean;
	isLoadingSession: boolean;
	isLoadingUserSessions: boolean;
	isAITyping: boolean;
	isDeletingSession: boolean;
	selectedModel: "claude" | "gemini" | "journey_ai";

	// Error states
	createSessionError: string | null;
	sendMessageError: string | null;
	loadSessionError: string | null;
	loadUserSessionsError: string | null;
	deleteSessionError: string | null;
}

const initialState: ChatState = {
	currentSessionId: null,
	currentSessionTitle: null,
	messages: [],
	totalMessages: 0,
	hasMoreMessages: false,
	userSessions: [],
	totalSessions: 0,
	hasMoreSessions: false,
	isCreatingSession: false,
	isSendingMessage: false,
	isLoadingSession: false,
	isLoadingUserSessions: false,
	isAITyping: false,
	createSessionError: null,
	sendMessageError: null,
	loadSessionError: null,
	loadUserSessionsError: null,
	isDeletingSession: false,
	deleteSessionError: null,
	selectedModel: "claude" // Default to Claude 3 as specified in existing code comments
};

export default function chatReducer(state = initialState, action: any): ChatState {
	switch (action.type) {
		// Create Session
		case CHAT_CREATE_SESSION_REQUEST:
			return {
				...state,
				isCreatingSession: true,
				createSessionError: null
			};

		case CHAT_CREATE_SESSION_SUCCESS:
			return {
				...state,
				isCreatingSession: false,
				currentSessionId: action.payload.sessionId,
				currentSessionTitle: action.payload.sessionTitle,
				createSessionError: null,
				messages: [] // Clear messages for new session
			};

		case CHAT_CREATE_SESSION_FAILURE:
			return {
				...state,
				isCreatingSession: false,
				createSessionError: action.payload
			};

		// Send Message
		case CHAT_SEND_MESSAGE_REQUEST:
			return {
				...state,
				isSendingMessage: true,
				sendMessageError: null
			};

		case CHAT_SEND_MESSAGE_SUCCESS:
			// Filter out optimistic messages and add real messages from API
			const filteredMessages = state.messages.filter(msg => !msg.isOptimistic);
			return {
				...state,
				isSendingMessage: false,
				messages: [
					...filteredMessages,
					action.payload.userMessage,
					action.payload.aiResponse
				],
				totalMessages: state.totalMessages + 2,
				sendMessageError: null
			};

		case CHAT_SEND_MESSAGE_FAILURE:
			return {
				...state,
				isSendingMessage: false,
				sendMessageError: action.payload
			};

		// Load Session
		case CHAT_LOAD_SESSION_REQUEST:
			return {
				...state,
				isLoadingSession: true,
				loadSessionError: null
			};

		case CHAT_LOAD_SESSION_SUCCESS:
			return {
				...state,
				isLoadingSession: false,
				currentSessionId: action.payload.sessionId,
				currentSessionTitle: action.payload.sessionTitle,
				messages: action.payload.messages,
				totalMessages: action.payload.totalMessages,
				hasMoreMessages: action.payload.hasMore,
				loadSessionError: null
			};

		case CHAT_LOAD_SESSION_FAILURE:
			return {
				...state,
				isLoadingSession: false,
				loadSessionError: action.payload
			};

		// Load User Sessions
		case CHAT_LOAD_USER_SESSIONS_REQUEST:
			return {
				...state,
				isLoadingUserSessions: true,
				loadUserSessionsError: null
			};

		case CHAT_LOAD_USER_SESSIONS_SUCCESS:
			return {
				...state,
				isLoadingUserSessions: false,
				userSessions: action.payload.sessions,
				totalSessions: action.payload.totalSessions,
				hasMoreSessions: action.payload.hasMore,
				loadUserSessionsError: null
			};

		case CHAT_LOAD_USER_SESSIONS_FAILURE:
			return {
				...state,
				isLoadingUserSessions: false,
				loadUserSessionsError: action.payload
			};

		// Set Current Session
		case CHAT_SET_CURRENT_SESSION:
			return {
				...state,
				currentSessionId: action.payload
			};

		// Clear Messages
		case CHAT_CLEAR_MESSAGES:
			return {
				...state,
				messages: [],
				totalMessages: 0,
				hasMoreMessages: false
			};

		// Set AI Typing
		case CHAT_SET_AI_TYPING:
			return {
				...state,
				isAITyping: action.payload
			};

		// Reset State
		case CHAT_RESET_STATE:
			return initialState;

		// Optimistic UI Actions
		case CHAT_ADD_OPTIMISTIC_MESSAGE:
			return {
				...state,
				messages: [...state.messages, action.payload]
			};

		case CHAT_UPDATE_MESSAGE_STATUS:
			return {
				...state,
				messages: state.messages.map(msg =>
					msg.message_id === action.payload.messageId
						? { ...msg, status: action.payload.status }
						: msg
				)
			};

		case CHAT_REMOVE_OPTIMISTIC_MESSAGE:
			return {
				...state,
				messages: state.messages.filter(msg => msg.message_id !== action.payload)
			};

		// Delete Session
		case CHAT_DELETE_SESSION_REQUEST:
			return {
				...state,
				isDeletingSession: true,
				deleteSessionError: null
			};

		case CHAT_DELETE_SESSION_SUCCESS:
			const updatedSessions = state.userSessions.filter(s => s.id !== action.payload);
			const isCurrentSessionDeleted = state.currentSessionId === action.payload;
			return {
				...state,
				isDeletingSession: false,
				userSessions: updatedSessions,
				totalSessions: state.totalSessions - 1,
				currentSessionId: isCurrentSessionDeleted ? null : state.currentSessionId,
				currentSessionTitle: isCurrentSessionDeleted ? null : state.currentSessionTitle,
				messages: isCurrentSessionDeleted ? [] : state.messages,
				deleteSessionError: null
			};

		case CHAT_DELETE_SESSION_FAILURE:
			return {
				...state,
				isDeletingSession: false,
				deleteSessionError: action.payload
			};

		case CHAT_SET_SELECTED_MODEL:
			return {
				...state,
				selectedModel: action.payload
			};

		default:
			return state;
	}
}